import { useContext, useEffect, useMemo, useState } from 'react';
import { Chip, Stack, Typography } from '@mui/material';
import DataGrid from 'common/mui-data-grid/lib/components/DataGrid';
import { ExpandMore, ChevronRight } from '@mui/icons-material';
import { CustomDataGridToolbar } from 'common/mui-data-grid/lib/components/CustomDataGridToolbar';
import moment from 'moment';
import { DF_HUMAN } from 'helpers/dateFormats';
import {
  useGetCall,
  useLazyDeleteCallWithURL,
  useLazyGetCallWithURL,
} from 'common/api/useApiCall';
import { URLS } from 'constants/URL';
import { adaptSilenceAlertsResponse } from './adaptSilenceAlertsResponse';
import { SilenceAlertsModal } from 'common/Modals/SilenceAlertsModal/SilenceAlertsModal';
import { NotificationContext } from 'common/NotificationContainer/NotificationContainer';
import ConfirmationDialog from 'common/Modals/ConfirmationDialog';
import { createParams } from 'helpers/createParams';

const SilenceAlertsV2 = () => {
  const [openModal, setOpenModal] = useState(false);
  const [multiple, setMultiple] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState(null);
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState(false);
  const { addNotification } = useContext(NotificationContext);

  const { response: tenantIdNamesResponse } = useGetCall(
    `${URLS.getTenantIdNames}with-id=true`,
  );

  const tenants = useMemo(() => {
    return tenantIdNamesResponse?.response?.length
      ? tenantIdNamesResponse?.response?.map((_t) => ({
          id: _t?._id,
          name: _t?.domain,
          pgSource: _t?.pgSource,
        }))
      : [];
  }, [tenantIdNamesResponse]);

  const defaultTenantId = useMemo(() => {
    return tenants?.length > 0 ? tenants[0]?.id : null;
  }, [tenants]);

  const [filters, setFilters] = useState<any>({
    'tenant-id': null,
  });

  useEffect(() => {
    if (defaultTenantId && !filters['tenant-id']) {
      setFilters((prev) => ({ ...prev, 'tenant-id': defaultTenantId }));
    }
  }, [defaultTenantId]);

  const tenantDomain = useMemo(() => {
    if (!filters['tenant-id']) return null;
    const tenant = tenants.find((t) => t.id === filters['tenant-id']);
    return tenant?.name || null;
  }, [filters['tenant-id'], tenants]);

  const tenantPgSource = useMemo(() => {
    if (!filters['tenant-id']) return null;
    const tenant = tenants.find((t) => t.id === filters['tenant-id']);
    return tenant?.pgSource || null;
  }, [filters['tenant-id'], tenants]);

  const {
    response: alerts,
    isLoading,
    postData: fetchAlerts,
  } = useLazyGetCallWithURL(adaptSilenceAlertsResponse);

  useEffect(() => {
    if (filters['tenant-id']) {
      const apiParams = {
        'tenant-id': filters['tenant-id'],
        ...(tenantDomain && { domain: tenantDomain }),
        ...(tenantPgSource && { pgSource: tenantPgSource }),
      };
      const queryString = createParams(apiParams).replace(/^&/, '?');
      const url = `${URLS.getAlertSilencesV2}${queryString}`;
      fetchAlerts(url);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters['tenant-id'], tenantDomain, tenantPgSource]);

  const {
    response: expireAlertResponse,
    isLoading: expireAlertLoading,
    postData: expireAlert,
    responseStatus: expireAlertResponseStatus,
    error: expireAlertError,
  } = useLazyDeleteCallWithURL();

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: 'ID',
        hide: true,
      },
      {
        field: 'startsAt',
        headerName: 'Start At',
        width: 150,
        renderCell: (params) => {
          const { row: data } = params;
          const { startsAt } = data;
          const _date = moment(startsAt).format(DF_HUMAN);
          if (!_date) {
            return '';
          }
          return <Stack>{_date}</Stack>;
        },
      },
      {
        field: 'endsAt',
        headerName: 'Ends At',
        width: 150,
        renderCell: (params) => {
          const { row: data } = params;
          const { endsAt } = data;
          const _date = moment(endsAt).format(DF_HUMAN);
          if (!_date) {
            return '';
          }
          return <Stack>{_date}</Stack>;
        },
      },

      {
        field: 'matchers',
        headerName: 'Matchers',
        width: 300,
        renderCell: (params) => {
          const { row: data } = params;
          const { matchers } = data;
          return matchers?.length ? (
            <Stack direction="row" flexWrap="wrap" spacing={1}>
              {matchers?.map((_p) => (
                <Stack py={1}>
                  <Chip label={`${_p?.name}: ${_p?.value}`} />
                </Stack>
              ))}
            </Stack>
          ) : (
            '-'
          );
        },
      },
      {
        field: 'creator',
        headerName: 'Created by',
      },
    ],
    [],
  );

  const actionProps = useMemo(
    () => [
      {
        label: 'Silence Alert',
        onClick: (e) => {
          setOpenModal(true);
        },
      },
      {
        label: 'Bulk Silence Alerts',
        onClick: (e) => {
          setMultiple(true);
          setOpenModal(true);
        },
      },
    ],
    [],
  );

  const menuItemProps = useMemo(
    () => [
      {
        label: 'Unsilence Alert',
        onClick: (_alert) => {
          setSelectedAlert(_alert);
          setOpenDeleteConfirmation(true);
        },
      },
    ],
    [],
  );

  const filterProps = useMemo(
    () => [
      {
        name: 'Tenant',
        column: 'tenant',
        initialVal: filters?.['tenant-id'] || defaultTenantId || '',
        options: tenants,
        onChange: (value) => handleFilterChange('tenant-id', value),
        type: 'singleSelect', // Searchable single select
      },
    ],
    [filters, tenants, defaultTenantId],
  );

  const handleFilterChange = (filterKey, val) => {
    if (filterKey && val) {
      setFilters((prev) => ({ ...prev, [filterKey]: val }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      'tenant-id': defaultTenantId,
    });
  };

  useEffect(() => {
    if (expireAlertResponse?.status === 'SUCCESSFUL') {
      addNotification({
        severity: 'success',
        message: `${expireAlertResponse?.message}`,
      });
      setOpenDeleteConfirmation(false);
      if (filters['tenant-id']) {
        const apiParams = {
          'tenant-id': filters['tenant-id'],
          ...(tenantDomain && { domain: tenantDomain }),
          ...(tenantPgSource && { pgSource: tenantPgSource }),
        };
        const queryString = createParams(apiParams).replace(/^&/, '?');
        const url = `${URLS.getAlertSilencesV2}${queryString}`;
        fetchAlerts(url);
      }
    } else if (expireAlertResponse?.status) {
      addNotification({
        severity: 'error',
        message: expireAlertResponse?.message || 'Something went wrong',
      });
      setOpenDeleteConfirmation(false);
    } else if (
      expireAlertResponse === null &&
      expireAlertResponseStatus > 200
    ) {
      addNotification({
        severity: 'error',
        message: expireAlertError?.message,
      });
      setOpenDeleteConfirmation(false);
    }
  }, [
    expireAlertResponse,
    expireAlertResponseStatus,
    expireAlertError,
  ]);

  const handleOnSuccess = () => {
    setOpenModal(false);
    if (filters['tenant-id']) {
      const apiParams = {
        'tenant-id': filters['tenant-id'],
        ...(tenantDomain && { domain: tenantDomain }),
        ...(tenantPgSource && { pgSource: tenantPgSource }),
      };
      const queryString = createParams(apiParams).replace(/^&/, '?');
      const url = `${URLS.getAlertSilencesV2}${queryString}`;
      fetchAlerts(url);
    }
  };

  const onClose = (val) => {
    setOpenModal(val);
    setMultiple(false);
  };

  return (
    <Stack sx={{ height: '100vh' }}>
      <DataGrid
        columns={columns}
        rows={alerts}
        loading={isLoading}
        styleOverrides={{ hideShadow: true }}
        actions={actionProps}
        menuItems={menuItemProps}
        components={{
          Toolbar: CustomDataGridToolbar,
          DetailPanelCollapseIcon: ExpandMore,
          DetailPanelExpandIcon: ChevronRight,
        }}
        componentsProps={{
          toolbar: { showQuickFilter: true },
        }}
        filters={filterProps}
        apiFilter
        onResetFilters={handleResetFilters}
      />
      {openModal && (
        <SilenceAlertsModal
          open={openModal}
          setOpen={onClose}
          handleOnSuccess={handleOnSuccess}
          bulkSilence={multiple}
          includeDomain={true}
          silenceAlertsURL={URLS.silenceAlertsV2URL}
        />
      )}

      <ConfirmationDialog
        title="Unsilence Alert"
        open={openDeleteConfirmation}
        setOpen={setOpenDeleteConfirmation}
        handleConfirm={() => {
          if (!selectedAlert?.id) {
            return;
          }
          // Construct URL with silenceId and domain as query parameters
          // silenceId comes from selectedAlert.id (which is the _id from the API response)
          const apiParams = {
            silenceId: selectedAlert.id,
            ...(tenantDomain && { domain: tenantDomain }),
            ...(tenantPgSource && { pgSource: tenantPgSource }),
          };
          // createParams returns a string starting with '&', so we replace the first '&' with '?'
          const queryString = createParams(apiParams).replace(/^&/, '?');
          const url = `${URLS.expireSilenceV2}${queryString}`;
          expireAlert(url);
        }}
        confirmContent={
          <Typography>
            Are you sure you want to unsilence this alert?
          </Typography>
        }
        isLoading={expireAlertLoading}
      />
    </Stack>
  );
};

export default SilenceAlertsV2;
